import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ChevronLeft,
  ExternalLink,
  Folder as FolderIcon,
  FolderPlus,
  Globe2,
  LayoutGrid,
  Link as LinkIcon,
  List as ListIcon,
  LogOut,
  Lock,
  Maximize2,
  Menu,
  Minimize2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Star,
  Upload,
  FileText,
  Trash2,
  Users,
  X,
} from 'lucide-react'
import { fetchSession, loginWithGoogle, logout, type AuthSession } from './auth'
import { captureAndUploadThumbnail } from './thumbnail'
import {
  PERMISSION_HELP,
  PERMISSION_LABEL,
  SORT_LABEL,
  TAB_LABEL,
  createArtifact,
  createFolder,
  deleteArtifact,
  deleteFolder,
  formatBytes,
  formatRelative,
  getArtifact,
  listArtifacts,
  listFolders,
  reorderFolders,
  setStar,
  suggestUsers,
  updateArtifact,
  type Artifact,
  type ArtifactSort,
  type ArtifactType,
  type Folder,
  type Permission,
  type Tab,
  type UserSuggestion,
} from './api'

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; session: AuthSession }
  | { status: 'error'; message: string }

type View =
  | { kind: 'list' }
  | { kind: 'new'; initialFolderId: string | null }

const TABS: Tab[] = ['mine', 'shared', 'starred']
const ALLOWED_DOMAIN = 'leferi.com'
const PORTAL_URL = import.meta.env.DEV
  ? 'http://localhost:5173'
  : 'https://apps.leferi.com'

type ViewMode = 'grid' | 'list'
const VIEW_MODE_STORAGE_KEY = 'leferi.artifact.viewMode'

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' })
  const [view, setView] = useState<View>({ kind: 'list' })

  useEffect(() => {
    fetchSession()
      .then((session) =>
        setAuth(session ? { status: 'signed-in', session } : { status: 'signed-out' }),
      )
      .catch((err: unknown) =>
        setAuth({ status: 'error', message: err instanceof Error ? err.message : String(err) }),
      )
  }, [])

  return (
    <div className="min-h-full bg-surface-base">
      <Header auth={auth} onLogout={() => void handleLogout(setAuth, setView)} />
      <main className="mx-auto max-w-[1280px] px-s-6 pb-s-11">
        <Body auth={auth} view={view} setView={setView} />
      </main>
    </div>
  )
}

async function handleLogout(setAuth: (s: AuthState) => void, setView: (v: View) => void) {
  await logout()
  setAuth({ status: 'signed-out' })
  setView({ kind: 'list' })
}

function Header({ auth, onLogout }: { auth: AuthState; onLogout: () => void }) {
  return (
    <header className="bg-brand-blue text-fg-inverse">
      <div className="mx-auto flex max-w-[1280px] items-center justify-between px-s-6 py-s-4">
        <div className="flex items-center gap-s-5">
          <a
            href={PORTAL_URL}
            className="inline-flex items-center gap-s-1 rounded-r-1 bg-white/10 px-s-3 py-1 lf-caption text-fg-inverse transition-colors duration-base ease-standard hover:bg-white/20"
            title="Back to Leferi Apps"
          >
            <ChevronLeft size={12} strokeWidth={1.75} />
            Leferi Apps
          </a>
          <a href="/" className="flex items-center gap-s-2 text-fg-inverse">
            <span aria-hidden className="inline-block h-2 w-2 bg-brand-pink" />
            <span className="lf-wordmark text-[18px] text-fg-inverse">Artifacts</span>
          </a>
        </div>
        {auth.status === 'signed-in' ? (
          <SignedInChip session={auth.session} onLogout={onLogout} />
        ) : null}
      </div>
    </header>
  )
}

function SignedInChip({
  session,
  onLogout,
}: {
  session: AuthSession
  onLogout: () => void
}) {
  return (
    <div className="flex items-center gap-s-2 lf-body-sm sm:gap-s-3">
      <Avatar session={session} />
      <span className="hidden max-w-[180px] truncate text-fg-inverse sm:inline">
        {session.name ?? session.email}
      </span>
      <button
        type="button"
        onClick={onLogout}
        aria-label="Sign out"
        title="Sign out"
        className="inline-flex h-7 w-7 items-center justify-center rounded-r-1 border border-line-on-dark text-fg-inverse transition-colors duration-base ease-standard hover:border-white/40 hover:bg-white/10"
      >
        <LogOut size={14} strokeWidth={1.5} />
      </button>
    </div>
  )
}

function Avatar({ session }: { session: AuthSession }) {
  const [broken, setBroken] = useState(false)
  if (session.picture && !broken) {
    return (
      <img
        src={session.picture}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className="h-7 w-7 rounded-r-pill bg-white/15 object-cover ring-1 ring-line-on-dark"
      />
    )
  }
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-r-pill bg-white/15 text-[11px] font-medium text-fg-inverse ring-1 ring-line-on-dark">
      {initialsOf(session)}
    </span>
  )
}

function initialsOf(session: AuthSession): string {
  const source = session.name?.trim() || session.email
  const letters = source
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
  return (letters || source[0] || '?').toUpperCase()
}

function Body({
  auth,
  view,
  setView,
}: {
  auth: AuthState
  view: View
  setView: (v: View) => void
}) {
  if (auth.status === 'loading') {
    return <div className="py-s-11 lf-body text-fg-3">Loading.</div>
  }
  if (auth.status === 'error') {
    return (
      <div className="py-s-11">
        <p className="lf-eyebrow text-status-danger">▪ Connection</p>
        <p className="mt-s-3 lf-body text-fg-1">Could not reach the server.</p>
        <p className="mt-s-1 lf-body-sm text-fg-3">{auth.message}</p>
      </div>
    )
  }
  if (auth.status === 'signed-out') return <SignInPanel />
  if (view.kind === 'new') {
    return (
      <UploadView
        initialFolderId={view.initialFolderId}
        onCancel={() => setView({ kind: 'list' })}
        onSaved={() => setView({ kind: 'list' })}
      />
    )
  }
  return (
    <ListView
      onNew={(initialFolderId) =>
        setView({ kind: 'new', initialFolderId })
      }
    />
  )
}

function SignInPanel() {
  return (
    <div className="flex min-h-[calc(100vh-200px)] items-center justify-center py-s-11">
      <div className="w-full max-w-[420px]">
        <p className="lf-eyebrow">▪ Sign in</p>
        <h1 className="mt-s-3 lf-display-2">Welcome.</h1>
        <p className="mt-s-4 lf-body">
          Sign in with your <span className="lf-num">@leferi.com</span> Google account to upload and share artifacts.
        </p>
        <button
          type="button"
          onClick={loginWithGoogle}
          className="mt-s-7 inline-flex w-full items-center justify-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 focus-visible:outline-none focus-visible:shadow-focus"
        >
          Sign in with Google
        </button>
      </div>
    </div>
  )
}

function ListView({
  onNew,
}: {
  onNew: (initialFolderId: string | null) => void
}) {
  const [tab, setTab] = useState<Tab>('mine')
  const [folderId, setFolderId] = useState<string | null>(null)
  const [items, setItems] = useState<Artifact[] | null>(null)
  const [folders, setFolders] = useState<Folder[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Artifact | null>(null)
  const [sharing, setSharing] = useState<Artifact | null>(null)
  // Preview can be either the sidebar drawer (compact) or expanded to full
  // viewport ("전체보기"). We track them together so the header expand
  // toggle and the row's View/Open buttons share one piece of state.
  const [previewing, setPreviewing] = useState<{
    artifact: Artifact
    expanded: boolean
  } | null>(null)
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [creatingParentId, setCreatingParentId] = useState<string | null>(null)
  const [folderName, setFolderName] = useState('')
  const [creatingError, setCreatingError] = useState<string | null>(null)
  const [folderBusy, setFolderBusy] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [sort, setSort] = useState<ArtifactSort>('updated')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'grid'
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return stored === 'list' || stored === 'grid' ? stored : 'grid'
  })

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode)
  }, [viewMode])

  // Debounce free-text search so we don't refetch on every keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query), 200)
    return () => window.clearTimeout(id)
  }, [query])

  const reload = useCallback(() => {
    setError(null)
    setItems(null)
    Promise.all([
      listArtifacts(tab, tab === 'mine' ? folderId : undefined, {
        q: debouncedQuery,
        sort,
      }),
      listFolders(),
    ])
      .then(([arts, folds]) => {
        setItems(arts)
        setFolders(folds)
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      )
  }, [tab, folderId, debouncedQuery, sort])

  useEffect(() => {
    reload()
  }, [reload])

  // Deep-link: ?share=<publicId> opens that artifact's share modal directly.
  // Used by the portal report flow ("외부에 공유하기"). Runs once post-auth.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const id = params.get('share')
    if (!id) return
    getArtifact(id)
      .then((a) => setSharing(a))
      .catch(() => {})
      .finally(() => {
        params.delete('share')
        const qs = params.toString()
        window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''))
      })
  }, [])

  useEffect(() => {
    if (tab !== 'mine' && folderId !== null) setFolderId(null)
  }, [tab, folderId])

  usePreviewSync(previewing?.artifact ?? null, items, (next) =>
    setPreviewing((curr) =>
      curr && next ? { ...curr, artifact: next } : curr,
    ),
  )

  const handleCreateFolder = useCallback(async () => {
    // Guard against rapid double-Enter — without this the first POST is
    // still in flight when the second fires, creating two folders with
    // the same name.
    if (folderBusy) return
    const trimmed = folderName.trim()
    if (!trimmed) return
    setFolderBusy(true)
    setCreatingError(null)
    try {
      await createFolder({ name: trimmed, parentId: creatingParentId })
      setFolderName('')
      setCreatingFolder(false)
      reload()
    } catch (err) {
      setCreatingError(err instanceof Error ? err.message : String(err))
    } finally {
      setFolderBusy(false)
    }
  }, [creatingParentId, folderBusy, folderName, reload])

  const handleMoveArtifact = useCallback(
    async (artifactId: string, targetFolderId: string | null) => {
      try {
        await updateArtifact(artifactId, { parentId: targetFolderId })
        reload()
      } catch (err) {
        alert(`Move failed: ${err instanceof Error ? err.message : String(err)}`)
      }
    },
    [reload],
  )

  const handleReorderFolder = useCallback(
    (sourceId: string, targetId: string, position: 'before' | 'after') => {
      void handleFolderReorderDrop(folders ?? [], sourceId, targetId, position, reload)
    },
    [folders, reload],
  )

  const currentFolder = useMemo(() => {
    if (folderId === null || !folders) return null
    return folders.find((f) => f.id === folderId) ?? null
  }, [folderId, folders])

  const heading =
    tab === 'mine'
      ? currentFolder?.name ?? 'All artifacts'
      : TAB_LABEL[tab]

  const sidebarProps = {
    active: tab === 'mine',
    folders: folders ?? [],
    currentFolderId: folderId,
    onSelectFolder: (id: string | null) => {
      if (tab !== 'mine') setTab('mine')
      setFolderId(id)
      setSidebarOpen(false)
    },
    creatingFolder,
    creatingParentId,
    folderName,
    setFolderName,
    creatingError,
    onStartCreate: (parentId: string | null) => {
      if (tab !== 'mine') setTab('mine')
      setCreatingParentId(parentId)
      setFolderName('')
      setCreatingError(null)
      setCreatingFolder(true)
    },
    onCancelCreate: () => {
      setCreatingFolder(false)
      setFolderName('')
      setCreatingError(null)
    },
    onCommitCreate: () => void handleCreateFolder(),
    onMoveArtifactToFolder: handleMoveArtifact,
    onReorderFolder: handleReorderFolder,
    onFolderDeleted: reload,
  }

  return (
    <div className="grid gap-s-7 pt-s-7 md:grid-cols-[240px_minmax(0,1fr)]">
      {/* Desktop sidebar — inline column. Hidden on mobile so the
          content owns the viewport; the hamburger toggles the drawer. */}
      <div className="hidden md:block">
        <Sidebar {...sidebarProps} />
      </div>

      {/* Mobile drawer */}
      {sidebarOpen ? (
        <div
          className="fixed inset-0 z-40 flex md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-brand-brown/30" />
          <aside
            className="relative flex h-full w-[280px] max-w-[80vw] flex-col bg-surface-base shadow-lf-pop"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Dedicated top bar so the close button doesn't collide with
                the Sidebar's own ▪ Folders + ＋ header row. */}
            <div className="flex items-center justify-end border-b border-line-hairline px-s-3 py-s-2">
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                aria-label="Close folders"
                className="inline-flex h-8 w-8 items-center justify-center text-fg-3 hover:bg-surface-soft hover:text-fg-1"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-s-5 pb-s-5 pt-s-4">
              <Sidebar {...sidebarProps} />
            </div>
          </aside>
        </div>
      ) : null}

      <section className="min-w-0">
        {/* Mobile-only folder toggle. Sits above the page heading on small
            screens so the user can reach the folder tree without scrolling. */}
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="mb-s-4 inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline px-s-3 py-s-2 lf-body-sm text-fg-1 transition-colors duration-base ease-standard hover:bg-surface-soft md:hidden"
        >
          <Menu size={14} strokeWidth={1.75} />
          Folders
        </button>

        <div className="flex flex-col items-start gap-s-4 sm:flex-row sm:items-end sm:justify-between sm:gap-s-6">
          <div className="min-w-0">
            <p className="lf-eyebrow">▪ Library</p>
            <h1 className="mt-s-3 lf-display-2 truncate">{heading}.</h1>
            <p className="mt-s-2 lf-body-sm text-fg-3">
              Claude-generated HTML, organized into folders and shared as links.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onNew(tab === 'mine' ? folderId : null)}
            className="inline-flex shrink-0 items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500"
          >
            <Plus size={16} strokeWidth={1.75} />
            New artifact
          </button>
        </div>

        {/* Mobile: search owns its own row (so the icon-prefixed input
            doesn't truncate); sort + view toggle drop to a second row.
            sm+ folds everything back into one. */}
        <div className="mt-s-7 flex flex-col gap-s-3 sm:flex-row sm:flex-wrap sm:items-center">
          <SearchInput value={query} onChange={setQuery} />
          <div className="flex items-center gap-s-3">
            <SortSelect value={sort} onChange={setSort} />
            <ViewToggle mode={viewMode} setMode={setViewMode} />
          </div>
        </div>

        <div className="mt-s-5 flex items-end justify-between gap-s-3 border-b border-line-hairline">
          <div className="flex items-center gap-s-1">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`relative -mb-px px-s-4 py-s-3 lf-body-sm transition-colors duration-base ease-standard ${
                  tab === t
                    ? 'text-fg-1 after:absolute after:inset-x-s-4 after:bottom-0 after:h-[2px] after:bg-brand-pink'
                    : 'text-fg-3 hover:text-fg-1'
                }`}
              >
                {TAB_LABEL[t]}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-s-5">
          {items === null && !error ? (
            <p className="lf-body text-fg-3">Loading.</p>
          ) : error ? (
            <p className="lf-body text-status-danger">▪ {error}</p>
          ) : items && items.length === 0 ? (
            debouncedQuery ? (
              <NoResultsState query={debouncedQuery} />
            ) : (
              <EmptyState tab={tab} inFolder={folderId !== null} />
            )
          ) : viewMode === 'grid' ? (
            <ArtifactGrid
              items={items ?? []}
              folders={folders ?? []}
              onChanged={reload}
              onEdit={setEditing}
              onShare={setSharing}
              onPreview={(artifact, expanded = false) =>
                setPreviewing({ artifact, expanded })
              }
            />
          ) : (
            <ArtifactList
              items={items ?? []}
              folders={folders ?? []}
              onChanged={reload}
              onEdit={setEditing}
              onShare={setSharing}
              onPreview={(artifact, expanded = false) =>
                setPreviewing({ artifact, expanded })
              }
            />
          )}
        </div>

        {editing ? (
          <EditModal
            artifact={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null)
              reload()
            }}
          />
        ) : null}
        {sharing ? (
          <ShareModal
            artifact={sharing}
            onClose={() => setSharing(null)}
            onSaved={() => {
              setSharing(null)
              reload()
            }}
          />
        ) : null}
      </section>

      {previewing ? (
        <PreviewPanel
          artifact={previewing.artifact}
          expanded={previewing.expanded}
          onToggleExpand={() =>
            setPreviewing((curr) =>
              curr ? { ...curr, expanded: !curr.expanded } : curr,
            )
          }
          onClose={() => setPreviewing(null)}
          onEdit={() => setEditing(previewing.artifact)}
          onShare={() => setSharing(previewing.artifact)}
        />
      ) : null}
    </div>
  )
}

// Keep the side preview header in sync with edits made from the stacked
// modal. When the list reloads after a save, find the same artifact by id
// and swap it into `previewing` so title / description / size refresh.
function usePreviewSync(
  previewing: Artifact | null,
  items: Artifact[] | null,
  setPreviewing: (a: Artifact | null) => void,
) {
  useEffect(() => {
    if (!previewing || !items) return
    const fresh = items.find((a) => a.id === previewing.id)
    if (fresh && fresh !== previewing) setPreviewing(fresh)
  }, [items, previewing, setPreviewing])
}

const DRAG_MIME = 'application/x-leferi-artifact'
const FOLDER_DRAG_MIME = 'application/x-leferi-folder'

interface FolderTreeNode {
  folder: Folder
  children: FolderTreeNode[]
}

function buildFolderTree(folders: Folder[]): FolderTreeNode[] {
  const byParent = new Map<string | null, Folder[]>()
  for (const f of folders) {
    const list = byParent.get(f.parentId) ?? []
    list.push(f)
    byParent.set(f.parentId, list)
  }
  // Sort by owner-controlled order, then name as a tie-break.
  for (const list of byParent.values()) {
    list.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order
      return a.name.localeCompare(b.name)
    })
  }
  const build = (parentId: string | null): FolderTreeNode[] =>
    (byParent.get(parentId) ?? []).map((folder) => ({
      folder,
      children: build(folder.id),
    }))
  return build(null)
}

async function handleFolderReorderDrop(
  folders: Folder[],
  sourceId: string,
  targetId: string,
  position: 'before' | 'after',
  onChanged: () => void,
): Promise<void> {
  const source = folders.find((f) => f.id === sourceId)
  const target = folders.find((f) => f.id === targetId)
  // Reorder only happens between same-parent siblings — cross-parent moves
  // already go through the artifact drop path or the folder PATCH endpoint.
  if (!source || !target || source.parentId !== target.parentId) return
  if (source.id === target.id) return

  const siblings = folders
    .filter((f) => f.parentId === source.parentId)
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order
      return a.name.localeCompare(b.name)
    })

  const withoutSource = siblings.filter((f) => f.id !== sourceId)
  const insertIndex =
    withoutSource.findIndex((f) => f.id === targetId) +
    (position === 'after' ? 1 : 0)
  const next = [
    ...withoutSource.slice(0, insertIndex),
    source,
    ...withoutSource.slice(insertIndex),
  ]

  try {
    await reorderFolders(
      source.parentId,
      next.map((f) => f.id),
    )
    onChanged()
  } catch (err) {
    alert(`Reorder failed: ${err instanceof Error ? err.message : String(err)}`)
  }
}

function Sidebar({
  active,
  folders,
  currentFolderId,
  onSelectFolder,
  creatingFolder,
  creatingParentId,
  folderName,
  setFolderName,
  creatingError,
  onStartCreate,
  onCancelCreate,
  onCommitCreate,
  onMoveArtifactToFolder,
  onReorderFolder,
  onFolderDeleted,
}: {
  active: boolean
  folders: Folder[]
  currentFolderId: string | null
  onSelectFolder: (id: string | null) => void
  creatingFolder: boolean
  creatingParentId: string | null
  folderName: string
  setFolderName: (s: string) => void
  creatingError: string | null
  onStartCreate: (parentId: string | null) => void
  onCancelCreate: () => void
  onCommitCreate: () => void
  onMoveArtifactToFolder: (artifactId: string, folderId: string | null) => void
  onReorderFolder: (sourceId: string, targetId: string, position: 'before' | 'after') => void
  onFolderDeleted: () => void
}) {
  const tree = useMemo(() => buildFolderTree(folders), [folders])

  return (
    <aside className="md:sticky md:top-s-7 md:max-h-[calc(100vh-160px)] md:overflow-y-auto">
      <div className="flex items-center justify-between pb-s-3">
        <p className="lf-eyebrow">▪ Folders</p>
        <button
          type="button"
          onClick={() => onStartCreate(null)}
          disabled={!active}
          aria-label="New folder at root"
          title="New folder"
          className="inline-flex h-6 w-6 items-center justify-center rounded-r-1 text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FolderPlus size={14} strokeWidth={1.5} />
        </button>
      </div>

      <SidebarFolderRow
        label="All artifacts"
        folderId={null}
        depth={0}
        selected={currentFolderId === null}
        active={active}
        onSelect={onSelectFolder}
        onMoveArtifactToFolder={onMoveArtifactToFolder}
      />

      {creatingFolder && creatingParentId === null ? (
        <SidebarCreateInput
          depth={1}
          folderName={folderName}
          setFolderName={setFolderName}
          creatingError={creatingError}
          onCancel={onCancelCreate}
          onCommit={onCommitCreate}
        />
      ) : null}

      {tree.map((node) => (
        <SidebarTreeNode
          key={node.folder.id}
          node={node}
          depth={1}
          currentFolderId={currentFolderId}
          active={active}
          onSelectFolder={onSelectFolder}
          onStartCreate={onStartCreate}
          onMoveArtifactToFolder={onMoveArtifactToFolder}
          onReorderFolder={onReorderFolder}
          onFolderDeleted={onFolderDeleted}
          creatingFolder={creatingFolder}
          creatingParentId={creatingParentId}
          folderName={folderName}
          setFolderName={setFolderName}
          creatingError={creatingError}
          onCancelCreate={onCancelCreate}
          onCommitCreate={onCommitCreate}
        />
      ))}

      {!active ? (
        <p className="mt-s-5 lf-caption text-fg-3">
          Folders apply to your own artifacts. Switch to Mine to organize.
        </p>
      ) : null}
    </aside>
  )
}

function SidebarTreeNode({
  node,
  depth,
  currentFolderId,
  active,
  onSelectFolder,
  onStartCreate,
  onMoveArtifactToFolder,
  onReorderFolder,
  onFolderDeleted,
  creatingFolder,
  creatingParentId,
  folderName,
  setFolderName,
  creatingError,
  onCancelCreate,
  onCommitCreate,
}: {
  node: FolderTreeNode
  depth: number
  currentFolderId: string | null
  active: boolean
  onSelectFolder: (id: string | null) => void
  onStartCreate: (parentId: string | null) => void
  onMoveArtifactToFolder: (artifactId: string, folderId: string | null) => void
  onReorderFolder: (sourceId: string, targetId: string, position: 'before' | 'after') => void
  onFolderDeleted: () => void
  creatingFolder: boolean
  creatingParentId: string | null
  folderName: string
  setFolderName: (s: string) => void
  creatingError: string | null
  onCancelCreate: () => void
  onCommitCreate: () => void
}) {
  return (
    <>
      <SidebarFolderRow
        label={node.folder.name}
        folderId={node.folder.id}
        depth={depth}
        selected={currentFolderId === node.folder.id}
        active={active}
        onSelect={onSelectFolder}
        onMoveArtifactToFolder={onMoveArtifactToFolder}
        onReorderFolder={onReorderFolder}
        onAddSubfolder={() => onStartCreate(node.folder.id)}
        onDeleted={onFolderDeleted}
        folder={node.folder}
      />
      {creatingFolder && creatingParentId === node.folder.id ? (
        <SidebarCreateInput
          depth={depth + 1}
          folderName={folderName}
          setFolderName={setFolderName}
          creatingError={creatingError}
          onCancel={onCancelCreate}
          onCommit={onCommitCreate}
        />
      ) : null}
      {node.children.map((child) => (
        <SidebarTreeNode
          key={child.folder.id}
          node={child}
          depth={depth + 1}
          currentFolderId={currentFolderId}
          active={active}
          onSelectFolder={onSelectFolder}
          onStartCreate={onStartCreate}
          onMoveArtifactToFolder={onMoveArtifactToFolder}
          onReorderFolder={onReorderFolder}
          onFolderDeleted={onFolderDeleted}
          creatingFolder={creatingFolder}
          creatingParentId={creatingParentId}
          folderName={folderName}
          setFolderName={setFolderName}
          creatingError={creatingError}
          onCancelCreate={onCancelCreate}
          onCommitCreate={onCommitCreate}
        />
      ))}
    </>
  )
}

function SidebarFolderRow({
  label,
  folderId,
  depth,
  selected,
  active,
  onSelect,
  onMoveArtifactToFolder,
  onReorderFolder,
  onAddSubfolder,
  onDeleted,
  folder,
}: {
  label: string
  folderId: string | null
  depth: number
  selected: boolean
  active: boolean
  onSelect: (id: string | null) => void
  onMoveArtifactToFolder: (artifactId: string, folderId: string | null) => void
  onReorderFolder?: (sourceId: string, targetId: string, position: 'before' | 'after') => void
  onAddSubfolder?: () => void
  onDeleted?: () => void
  folder?: Folder
}) {
  const [artifactDragOver, setArtifactDragOver] = useState(false)
  const [folderDropEdge, setFolderDropEdge] = useState<'before' | 'after' | null>(null)
  const [busy, setBusy] = useState(false)

  const handleDragStart = (e: React.DragEvent) => {
    if (!folder || !active) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData(FOLDER_DRAG_MIME, folder.id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (!active) return
    const types = e.dataTransfer.types
    if (types.includes(FOLDER_DRAG_MIME) && folder) {
      // Folder reorder — show a pink insert line above or below depending
      // on whether the cursor is in the top or bottom half of the row.
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      const rect = e.currentTarget.getBoundingClientRect()
      const edge: 'before' | 'after' =
        e.clientY - rect.top < rect.height / 2 ? 'before' : 'after'
      setFolderDropEdge(edge)
      return
    }
    if (types.includes(DRAG_MIME)) {
      // Artifact move — highlight the whole row.
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setArtifactDragOver(true)
    }
  }
  const handleDragLeave = () => {
    setArtifactDragOver(false)
    setFolderDropEdge(null)
  }
  const handleDrop = (e: React.DragEvent) => {
    if (!active) return
    const folderSource = e.dataTransfer.getData(FOLDER_DRAG_MIME)
    if (folderSource && folder && onReorderFolder) {
      e.preventDefault()
      const edge = folderDropEdge ?? 'after'
      setFolderDropEdge(null)
      if (folderSource !== folder.id) {
        onReorderFolder(folderSource, folder.id, edge)
      }
      return
    }
    const artifactSource = e.dataTransfer.getData(DRAG_MIME)
    setArtifactDragOver(false)
    if (!artifactSource) return
    e.preventDefault()
    onMoveArtifactToFolder(artifactSource, folderId)
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!folder || busy) return
    if (!confirm(`Delete folder "${folder.name}"? Only empty folders can be deleted.`)) return
    setBusy(true)
    try {
      await deleteFolder(folder.id)
      onDeleted?.()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      alert(
        msg === 'folder_not_empty'
          ? 'This folder has artifacts or subfolders inside. Move or delete them first.'
          : `Delete failed: ${msg}`,
      )
      setBusy(false)
    }
  }

  return (
    <div
      role="button"
      tabIndex={active ? 0 : -1}
      draggable={!!folder && active}
      onDragStart={handleDragStart}
      onClick={() => active && onSelect(folderId)}
      onKeyDown={(e) => {
        if (!active) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(folderId)
        }
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ paddingLeft: `${8 + depth * 12}px` }}
      className={`group relative flex cursor-pointer items-center gap-s-2 py-s-2 pr-s-2 lf-body-sm transition-colors duration-base ease-standard ${
        selected
          ? 'bg-surface-soft text-fg-1 before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-brand-pink'
          : 'text-fg-2 hover:bg-surface-soft hover:text-fg-1'
      } ${artifactDragOver ? 'outline outline-2 -outline-offset-2 outline-brand-pink' : ''} ${
        active ? '' : 'cursor-not-allowed opacity-50'
      }`}
    >
      {/* Folder reorder insert indicators — pink hairline above/below */}
      {folderDropEdge === 'before' ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-brand-pink"
        />
      ) : null}
      {folderDropEdge === 'after' ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-brand-pink"
        />
      ) : null}
      <FolderIcon
        size={14}
        strokeWidth={1.5}
        className={selected ? 'text-fg-1' : 'text-fg-3'}
        fill={folderId === null ? 'none' : selected ? 'currentColor' : 'none'}
      />
      <span className="flex-1 truncate">{label}</span>
      {folder && active ? (
        <div className="flex shrink-0 items-center opacity-0 transition-opacity duration-base ease-standard group-hover:opacity-100 focus-within:opacity-100">
          {onAddSubfolder ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onAddSubfolder()
              }}
              aria-label={`New subfolder in ${label}`}
              title="New subfolder"
              className="inline-flex h-6 w-6 items-center justify-center rounded-r-1 text-fg-3 hover:bg-surface-base hover:text-fg-1"
            >
              <Plus size={12} strokeWidth={1.75} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            aria-label={`Delete ${label}`}
            title="Delete folder (only if empty)"
            className="inline-flex h-6 w-6 items-center justify-center rounded-r-1 text-fg-3 hover:bg-surface-base hover:text-status-danger disabled:opacity-50"
          >
            <Trash2 size={12} strokeWidth={1.5} />
          </button>
        </div>
      ) : null}
    </div>
  )
}

function SidebarCreateInput({
  depth,
  folderName,
  setFolderName,
  creatingError,
  onCancel,
  onCommit,
}: {
  depth: number
  folderName: string
  setFolderName: (s: string) => void
  creatingError: string | null
  onCancel: () => void
  onCommit: () => void
}) {
  return (
    <div
      style={{ paddingLeft: `${8 + depth * 12}px` }}
      className="flex flex-col gap-s-1 py-s-2 pr-s-2"
    >
      <div className="flex items-center gap-s-2">
        <FolderPlus size={14} strokeWidth={1.5} className="text-fg-3" />
        <input
          autoFocus
          type="text"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onCommit()
            else if (e.key === 'Escape') onCancel()
          }}
          onBlur={() => {
            if (!folderName.trim()) onCancel()
          }}
          placeholder="Folder name"
          className="min-w-0 flex-1 border-0 border-b border-line-soft bg-transparent px-0 py-1 lf-body-sm text-fg-1 placeholder:text-fg-3 focus:border-line-strong focus:outline-none"
        />
      </div>
      {creatingError ? (
        <span className="lf-caption text-status-danger">{creatingError}</span>
      ) : null}
    </div>
  )
}

function SearchInput({
  value,
  onChange,
}: {
  value: string
  onChange: (s: string) => void
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 focus-within:border-line-strong focus-within:shadow-focus">
      <Search size={14} strokeWidth={1.5} className="shrink-0 text-fg-3" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search title or description"
        className="min-w-0 flex-1 border-0 bg-transparent lf-body-sm text-fg-1 placeholder:text-fg-3 focus:outline-none"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="inline-flex h-5 w-5 items-center justify-center text-fg-3 hover:text-fg-1"
        >
          <X size={12} strokeWidth={1.5} />
        </button>
      ) : null}
    </div>
  )
}

function SortSelect({
  value,
  onChange,
}: {
  value: ArtifactSort
  onChange: (s: ArtifactSort) => void
}) {
  return (
    <label className="inline-flex shrink-0 items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 focus-within:border-line-strong focus-within:shadow-focus">
      <span className="lf-caption text-fg-3">Sort</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ArtifactSort)}
        className="cursor-pointer border-0 bg-transparent pr-s-2 lf-body-sm text-fg-1 focus:outline-none"
      >
        {(['updated', 'created', 'views', 'title'] as ArtifactSort[]).map((s) => (
          <option key={s} value={s}>
            {SORT_LABEL[s]}
          </option>
        ))}
      </select>
    </label>
  )
}

function NoResultsState({ query }: { query: string }) {
  return (
    <div className="border border-line-hairline p-s-9 text-center">
      <p className="lf-eyebrow">▪ No matches</p>
      <p className="mt-s-3 lf-h3">
        Nothing matched &ldquo;{query}&rdquo;.
      </p>
      <p className="mt-s-2 lf-body-sm text-fg-3 mx-auto max-w-[420px]">
        Try a shorter phrase, or clear the search to see everything.
      </p>
    </div>
  )
}

function ViewToggle({
  mode,
  setMode,
}: {
  mode: ViewMode
  setMode: (m: ViewMode) => void
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 border border-line-hairline p-0.5">
      <button
        type="button"
        onClick={() => setMode('grid')}
        aria-label="Grid view"
        aria-pressed={mode === 'grid'}
        title="Grid view"
        className={`inline-flex h-7 w-7 items-center justify-center transition-colors duration-base ease-standard ${
          mode === 'grid' ? 'bg-brand-pink text-brand-brown' : 'text-fg-3 hover:text-fg-1'
        }`}
      >
        <LayoutGrid size={13} strokeWidth={1.75} />
      </button>
      <button
        type="button"
        onClick={() => setMode('list')}
        aria-label="List view"
        aria-pressed={mode === 'list'}
        title="List view"
        className={`inline-flex h-7 w-7 items-center justify-center transition-colors duration-base ease-standard ${
          mode === 'list' ? 'bg-brand-pink text-brand-brown' : 'text-fg-3 hover:text-fg-1'
        }`}
      >
        <ListIcon size={13} strokeWidth={1.75} />
      </button>
    </div>
  )
}

function EmptyState({ tab, inFolder }: { tab: Tab; inFolder: boolean }) {
  const copy: Record<Tab, { title: string; body: string }> = {
    mine: inFolder
      ? {
          title: 'Empty folder',
          body: 'Drag artifacts here from another folder, or use + New artifact.',
        }
      : {
          title: 'No artifacts yet',
          body: 'Drop a Claude-generated HTML in your first artifact and a sharable link is born.',
        },
    shared: {
      title: 'Nothing shared with you',
      body: 'When teammates share an artifact with you specifically — or with the whole leferi.com domain — it appears here.',
    },
    starred: {
      title: 'No starred artifacts',
      body: 'Click the star on any artifact to pin it here for quick access.',
    },
  }
  const c = copy[tab]
  return (
    <div className="border border-line-hairline p-s-9 text-center">
      <p className="lf-eyebrow">▪ Empty</p>
      <p className="mt-s-3 lf-h3">{c.title}.</p>
      <p className="mt-s-2 lf-body-sm text-fg-3 mx-auto max-w-[420px]">{c.body}</p>
    </div>
  )
}

function ArtifactGrid({
  items,
  folders,
  onChanged,
  onEdit,
  onShare,
  onPreview,
}: {
  items: Artifact[]
  folders: Folder[]
  onChanged: () => void
  onEdit: (a: Artifact) => void
  onShare: (a: Artifact) => void
  onPreview: (a: Artifact, expanded?: boolean) => void
}) {
  return (
    <ul className="grid grid-cols-1 gap-s-5 sm:grid-cols-2 xl:grid-cols-3">
      {items.map((a) => (
        <ArtifactCard
          key={a.id}
          artifact={a}
          folders={folders}
          onChanged={onChanged}
          onEdit={() => onEdit(a)}
          onShare={() => onShare(a)}
          onPreview={(expanded) => onPreview(a, expanded)}
        />
      ))}
    </ul>
  )
}

function ArtifactList({
  items,
  folders,
  onChanged,
  onEdit,
  onShare,
  onPreview,
}: {
  items: Artifact[]
  folders: Folder[]
  onChanged: () => void
  onEdit: (a: Artifact) => void
  onShare: (a: Artifact) => void
  onPreview: (a: Artifact, expanded?: boolean) => void
}) {
  return (
    <ul>
      {items.map((a) => (
        <ArtifactRow
          key={a.id}
          artifact={a}
          folders={folders}
          onChanged={onChanged}
          onEdit={() => onEdit(a)}
          onShare={() => onShare(a)}
          onPreview={(expanded) => onPreview(a, expanded)}
        />
      ))}
    </ul>
  )
}

function ArtifactCard({
  artifact,
  folders,
  onChanged,
  onEdit,
  onShare,
  onPreview,
}: {
  artifact: Artifact
  folders: Folder[]
  onChanged: () => void
  onEdit: () => void
  onShare: () => void
  onPreview: (expanded?: boolean) => void
}) {
  const [busy, setBusy] = useState(false)
  const [starred, setStarredLocal] = useState(artifact.isStarred)
  const [menuOpen, setMenuOpen] = useState(false)
  const [movePickerOpen, setMovePickerOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setStarredLocal(artifact.isStarred)
  }, [artifact.id, artifact.isStarred])

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
        setMovePickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const handleStar = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const next = !starred
    setStarredLocal(next)
    try {
      await setStar(artifact.id, next)
    } catch (err) {
      setStarredLocal(!next)
      alert(`Star failed: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuOpen(false)
    if (busy) return
    if (!confirm(`Delete "${artifact.title}"? This can't be undone.`)) return
    setBusy(true)
    try {
      await deleteArtifact(artifact.id)
      onChanged()
    } catch (err) {
      alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`)
      setBusy(false)
    }
  }

  const handleMove = async (target: string | null) => {
    setMovePickerOpen(false)
    setMenuOpen(false)
    try {
      await updateArtifact(artifact.id, { parentId: target })
      onChanged()
    } catch (err) {
      alert(`Move failed: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  const handleDragStart = (e: React.DragEvent) => {
    if (!artifact.isMine) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData(DRAG_MIME, artifact.id)
    e.dataTransfer.effectAllowed = 'move'
  }

  // Cmd/Ctrl/middle-click escape hatch — keep the "open in new tab" muscle
  // memory while plain clicks route through the side preview panel.
  // For link-type artifacts we skip the preview panel entirely (per design
  // decision) — every click opens the external URL in a new tab.
  const previewClick = (e: React.MouseEvent) => {
    if (artifact.type === 'link') return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
    e.preventDefault()
    onPreview()
  }

  return (
    <li
      draggable={artifact.isMine}
      onDragStart={handleDragStart}
      className="group relative flex flex-col border border-line-hairline bg-surface-base transition-[border-color,box-shadow] duration-base ease-standard hover:border-line-soft hover:shadow-lf-1"
    >
        <a
          href={artifact.viewUrl}
          target="_blank"
          rel="noreferrer"
          onClick={previewClick}
          onAuxClick={previewClick}
          className="block aspect-[4/3] overflow-hidden bg-surface-soft"
          aria-label={artifact.type === 'link' ? `Open ${artifact.title}` : `Preview ${artifact.title}`}
        >
          {artifact.type === 'link' ? (
            <LinkThumb
              url={artifact.linkUrl ?? artifact.viewUrl}
              ogImage={artifact.linkOgImageUrl}
              title={artifact.title}
            />
          ) : (
            <ThumbIframe url={artifact.viewUrl} title={artifact.title} />
          )}
        </a>

        <button
          type="button"
          onClick={handleStar}
          aria-label={starred ? 'Unstar' : 'Star'}
          title={starred ? 'Unstar' : 'Star'}
          className={`absolute right-s-2 top-s-2 inline-flex h-7 w-7 items-center justify-center rounded-r-1 backdrop-blur-sm transition-colors duration-base ease-standard ${
            starred
              ? 'bg-white/80 text-brand-pink'
              : 'bg-white/70 text-fg-3 opacity-0 group-hover:opacity-100 hover:text-fg-1 focus-visible:opacity-100'
          }`}
        >
          <Star size={14} strokeWidth={1.5} fill={starred ? 'currentColor' : 'none'} />
        </button>

        {artifact.isMine ? (
          <div ref={menuRef} className="absolute right-s-2 top-s-10">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setMenuOpen((v) => !v)
                setMovePickerOpen(false)
              }}
              aria-label="More actions"
              title="More"
              className="inline-flex h-7 w-7 items-center justify-center rounded-r-1 bg-white/70 text-fg-3 opacity-0 backdrop-blur-sm transition-colors duration-base ease-standard hover:text-fg-1 group-hover:opacity-100 focus-visible:opacity-100"
            >
              <MoreHorizontal size={14} strokeWidth={1.5} />
            </button>
            {menuOpen ? (
              movePickerOpen ? (
                <MoveToPicker
                  folders={folders}
                  currentParent={artifact.parentId}
                  onPick={(t) => void handleMove(t)}
                  onClose={() => {
                    setMovePickerOpen(false)
                    setMenuOpen(false)
                  }}
                />
              ) : (
                <div className="absolute right-0 top-9 z-10 w-[180px] border border-line-hairline bg-surface-base shadow-lf-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setMenuOpen(false)
                      onEdit()
                    }}
                    className="flex w-full items-center gap-s-2 px-s-3 py-s-2 text-left lf-body-sm text-fg-1 hover:bg-surface-soft"
                  >
                    <Pencil size={14} strokeWidth={1.5} className="text-fg-3" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setMenuOpen(false)
                      onShare()
                    }}
                    className="flex w-full items-center gap-s-2 px-s-3 py-s-2 text-left lf-body-sm text-fg-1 hover:bg-surface-soft"
                  >
                    <ExternalLink size={14} strokeWidth={1.5} className="text-fg-3" />
                    Share
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setMovePickerOpen(true)
                    }}
                    className="flex w-full items-center gap-s-2 px-s-3 py-s-2 text-left lf-body-sm text-fg-1 hover:bg-surface-soft"
                  >
                    <FolderIcon size={14} strokeWidth={1.5} className="text-fg-3" />
                    Move to folder
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={busy}
                    className="flex w-full items-center gap-s-2 border-t border-line-hairline px-s-3 py-s-2 text-left lf-body-sm text-status-danger hover:bg-surface-soft disabled:opacity-50"
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                    Delete
                  </button>
                </div>
              )
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-1 flex-col gap-s-2 border-t border-line-hairline px-s-4 py-s-3">
          <div className="flex items-start gap-s-2">
            <PermissionBadge permission={artifact.permission} />
            <a
              href={artifact.viewUrl}
              target="_blank"
              rel="noreferrer"
              onClick={previewClick}
              onAuxClick={previewClick}
              className="min-w-0 flex-1 truncate lf-h4 text-fg-1 hover:underline"
              title={artifact.title}
            >
              {artifact.title}
            </a>
          </div>
          <p className="lf-caption text-fg-3">
            {artifact.isMine ? (
              <>
                <span className="lf-num">{formatRelative(artifact.updatedAt)}</span>
                {' · '}
                <span className="lf-num">{formatBytes(artifact.sizeBytes)}</span>
                {' · '}
                <span className="lf-num">{artifact.viewCount}</span>{' '}
                {artifact.viewCount === 1 ? 'view' : 'views'}
              </>
            ) : (
              <>
                by <span>{artifact.ownerEmail}</span>
                {' · '}
                <span className="lf-num">{formatRelative(artifact.updatedAt)}</span>
              </>
            )}
          </p>
        </div>
    </li>
  )
}

function PreviewPanel({
  artifact,
  expanded,
  onToggleExpand,
  onClose,
  onEdit,
  onShare,
}: {
  artifact: Artifact
  expanded: boolean
  onToggleExpand: () => void
  onClose: () => void
  onEdit: () => void
  onShare: () => void
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-brand-brown/20"
      onClick={onClose}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="preview-title"
        onClick={(e) => e.stopPropagation()}
        className={`flex h-full w-full flex-col bg-surface-base shadow-lf-pop ${
          expanded ? '' : 'sm:w-[1100px] sm:max-w-[80vw]'
        }`}
      >
        <header className="flex items-center gap-s-3 border-b border-line-hairline px-s-5 py-s-3">
          <PermissionBadge permission={artifact.permission} />
          <div className="min-w-0 flex-1">
            <h2 id="preview-title" className="truncate lf-h4 text-fg-1" title={artifact.title}>
              {artifact.title}
            </h2>
            {artifact.description ? (
              <p className="truncate lf-caption text-fg-3">{artifact.description}</p>
            ) : null}
          </div>
          {artifact.isMine ? (
            <>
              <button
                type="button"
                onClick={onEdit}
                aria-label="Edit"
                title="Edit"
                className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1"
              >
                <Pencil size={14} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={onShare}
                aria-label="Share"
                title="Share"
                className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1"
              >
                <ExternalLink size={14} strokeWidth={1.5} />
              </button>
            </>
          ) : null}
          {/* Two separate actions per user feedback:
              · View (전체보기): toggle the panel between sidebar and full
                viewport — same iframe, more screen real estate.
              · New tab (새 창으로 열기): leave the app entirely. */}
          <button
            type="button"
            onClick={onToggleExpand}
            title={expanded ? 'Back to sidebar' : 'View full-screen'}
            className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1"
          >
            {expanded ? (
              <Minimize2 size={14} strokeWidth={1.5} />
            ) : (
              <Maximize2 size={14} strokeWidth={1.5} />
            )}
          </button>
          <a
            href={artifact.viewUrl}
            target="_blank"
            rel="noreferrer"
            title="Open in a new browser tab"
            className="inline-flex items-center rounded-r-1 px-s-3 py-s-1 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1"
          >
            New tab
          </a>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            title="Close (Esc)"
            className="inline-flex h-8 w-8 items-center justify-center text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </header>
        <iframe
          key={artifact.id}
          src={artifact.viewUrl}
          title={artifact.title}
          referrerPolicy="no-referrer"
          className="h-full w-full flex-1 border-0 bg-surface-base"
        />
      </aside>
    </div>
  )
}

function LinkThumb({
  url,
  ogImage,
  title,
}: {
  url: string
  ogImage?: string
  title: string
}) {
  // Link-type cards don't iframe the external site (X-Frame-Options blocks
  // it for most providers). If the server extracted og:image at upload we
  // hot-link it here — gives real previews for Notion/Docs/GitHub/news/etc.
  // Falls back to the pink-grey + hostname placeholder when no og:image
  // was found (internal dashboards, raw IPs, etc).
  const [broken, setBroken] = useState(false)
  let host = url
  try {
    host = new URL(url).hostname.replace(/^www\./, '')
  } catch {
    // fall through with raw url
  }

  if (ogImage && !broken) {
    return (
      <div className="relative h-full w-full overflow-hidden bg-surface-soft">
        <img
          src={ogImage}
          alt={`${title} preview`}
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setBroken(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Subtle host badge so the user still sees where this points */}
        <span className="absolute bottom-s-2 left-s-2 inline-flex items-center gap-1 rounded-r-1 bg-brand-brown/80 px-s-2 py-0.5 lf-caption text-fg-inverse backdrop-blur-sm">
          <LinkIcon size={10} strokeWidth={1.75} />
          <span className="lf-num">{host}</span>
        </span>
      </div>
    )
  }

  // Fallback when og:image is unavailable (internal dashboards, raw IPs,
  // pages without OpenGraph tags). Mirrors the LCMD content layout system:
  // hostname rides the top blue band in white ink; the user-given title
  // anchors the bottom lavender band in brand-brown ink. The 180° blue →
  // pink-grey gradient is a canonical BX pair (preview 09); contrast
  // bands are aligned with where the gradient is dark vs light so the
  // text reads cleanly in both halves.
  return (
    <div className="relative flex h-full w-full flex-col justify-between bg-grad-blue-grey p-s-4">
      <div className="flex items-center gap-s-2 text-white">
        <LinkIcon size={12} strokeWidth={1.75} aria-hidden />
        <span className="lf-eyebrow lf-num truncate text-white">{host}</span>
      </div>
      <h3
        className="font-text font-bold leading-[1.18] tracking-[-0.005em] text-brand-brown"
        style={{
          fontSize: 'clamp(15px, 2.2vw, 20px)',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {title}
      </h3>
    </div>
  )
}

function ThumbIframe({ url, title }: { url: string; title: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true)
            obs.disconnect()
            return
          }
        }
      },
      { rootMargin: '200px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div ref={ref} className="relative h-full w-full">
      {visible ? (
        <iframe
          src={url}
          title={`${title} preview`}
          loading="lazy"
          referrerPolicy="no-referrer"
          tabIndex={-1}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '400%',
            height: '400%',
            border: 0,
            transform: 'scale(0.25)',
            transformOrigin: 'top left',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-surface-soft to-surface-base" />
      )}
    </div>
  )
}

function ArtifactRow({
  artifact,
  folders,
  onChanged,
  onEdit,
  onShare,
  onPreview,
}: {
  artifact: Artifact
  folders: Folder[]
  onChanged: () => void
  onEdit: () => void
  onShare: () => void
  onPreview: (expanded?: boolean) => void
}) {
  const [busy, setBusy] = useState(false)
  const [starred, setStarredLocal] = useState(artifact.isStarred)
  const [movePickerOpen, setMovePickerOpen] = useState(false)

  useEffect(() => {
    setStarredLocal(artifact.isStarred)
  }, [artifact.id, artifact.isStarred])

  const handleStar = useCallback(async () => {
    const next = !starred
    setStarredLocal(next)
    try {
      await setStar(artifact.id, next)
    } catch (err) {
      setStarredLocal(!next)
      alert(`Star failed: ${err instanceof Error ? err.message : String(err)}`)
    }
  }, [artifact.id, starred])

  const handleDelete = useCallback(async () => {
    if (busy) return
    if (!confirm(`Delete "${artifact.title}"? This can't be undone.`)) return
    setBusy(true)
    try {
      await deleteArtifact(artifact.id)
      onChanged()
    } catch (err) {
      alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`)
      setBusy(false)
    }
  }, [artifact, busy, onChanged])

  const handleMove = useCallback(
    async (target: string | null) => {
      setMovePickerOpen(false)
      try {
        await updateArtifact(artifact.id, { parentId: target })
        onChanged()
      } catch (err) {
        alert(`Move failed: ${err instanceof Error ? err.message : String(err)}`)
      }
    },
    [artifact.id, onChanged],
  )

  const handleDragStart = (e: React.DragEvent) => {
    if (!artifact.isMine) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData(DRAG_MIME, artifact.id)
    e.dataTransfer.effectAllowed = 'move'
  }

  return (
    <li
      draggable={artifact.isMine}
      onDragStart={handleDragStart}
      className="group flex items-center gap-s-5 border-b border-line-hairline py-s-4 transition-colors duration-base ease-standard hover:bg-surface-soft"
    >
      <button
        type="button"
        onClick={() => void handleStar()}
        aria-label={starred ? 'Unstar' : 'Star'}
        title={starred ? 'Unstar' : 'Star'}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-r-1 transition-colors duration-base ease-standard ${
          starred ? 'text-brand-pink' : 'text-fg-3 hover:text-fg-1'
        }`}
      >
        <Star
          size={16}
          strokeWidth={1.5}
          fill={starred ? 'currentColor' : 'none'}
        />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-s-3">
          <PermissionBadge permission={artifact.permission} />
          <a
            href={artifact.viewUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              if (artifact.type === 'link') return
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
              e.preventDefault()
              onPreview()
            }}
            className="truncate lf-h4 text-fg-1 hover:underline"
          >
            {artifact.title}
          </a>
        </div>
        {artifact.description ? (
          <p className="mt-s-1 truncate lf-body-sm text-fg-3">{artifact.description}</p>
        ) : null}
        <p className="mt-s-1 lf-caption text-fg-3">
          {artifact.isMine ? (
            <>
              <span className="lf-num">{formatBytes(artifact.sizeBytes)}</span> ·{' '}
              {PERMISSION_LABEL[artifact.permission]}
              {artifact.recipients.length > 0
                ? ` · ${artifact.recipients.length} ${
                    artifact.recipients.length === 1 ? 'person' : 'people'
                  }`
                : ''}
            </>
          ) : (
            <>by {artifact.ownerEmail} · {PERMISSION_LABEL[artifact.permission]}</>
          )}
        </p>
      </div>
      <div className="shrink-0 lf-caption text-fg-3">{formatRelative(artifact.updatedAt)}</div>
      <div className="flex shrink-0 items-center gap-s-1">
        <button
          type="button"
          onClick={() => onPreview(true)}
          title="View full-screen inside Leferi Artifacts"
          className="inline-flex items-center rounded-r-1 px-s-2 py-s-1 lf-caption text-fg-2 transition-colors duration-base ease-standard hover:bg-surface-base hover:text-fg-1"
        >
          View
        </button>
        <a
          href={artifact.viewUrl}
          target="_blank"
          rel="noreferrer"
          title="Open in a new browser tab"
          className="inline-flex items-center rounded-r-1 px-s-2 py-s-1 lf-caption text-fg-2 transition-colors duration-base ease-standard hover:bg-surface-base hover:text-fg-1"
        >
          New tab
        </a>
        {artifact.isMine ? (
          <>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMovePickerOpen((v) => !v)}
                aria-label="Move to folder"
                title="Move to folder"
                className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-base hover:text-fg-1"
              >
                <FolderIcon size={14} strokeWidth={1.5} />
              </button>
              {movePickerOpen ? (
                <MoveToPicker
                  folders={folders}
                  currentParent={artifact.parentId}
                  onPick={(t) => void handleMove(t)}
                  onClose={() => setMovePickerOpen(false)}
                />
              ) : null}
            </div>
            <button
              type="button"
              onClick={onEdit}
              aria-label="Edit"
              title="Edit"
              className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-base hover:text-fg-1"
            >
              <Pencil size={14} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={onShare}
              aria-label="Share"
              title="Share"
              className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-base hover:text-fg-1"
            >
              <ExternalLink size={14} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              aria-label="Delete artifact"
              title="Delete"
              className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-base hover:text-status-danger disabled:opacity-50"
            >
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          </>
        ) : null}
      </div>
    </li>
  )
}

function MoveToPicker({
  folders,
  currentParent,
  onPick,
  onClose,
}: {
  folders: Folder[]
  currentParent: string | null
  onPick: (target: string | null) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  // Sort folders by display path so the picker is browseable.
  const sorted = useMemo(() => {
    const byId = new Map(folders.map((f) => [f.id, f]))
    return [...folders]
      .map((f) => ({ folder: f, path: pathOf(f, byId) }))
      .sort((a, b) => a.path.localeCompare(b.path))
  }, [folders])

  return (
    <div
      ref={ref}
      className="absolute right-0 top-9 z-10 w-[260px] border border-line-hairline bg-surface-base shadow-lf-2"
    >
      <div className="border-b border-line-hairline px-s-3 py-s-2 lf-eyebrow">▪ Move to</div>
      <ul className="max-h-[280px] overflow-y-auto py-s-1">
        <li>
          <button
            type="button"
            onClick={() => onPick(null)}
            disabled={currentParent === null}
            className="flex w-full items-center gap-s-2 px-s-3 py-s-2 text-left lf-body-sm text-fg-1 hover:bg-surface-soft disabled:cursor-default disabled:opacity-50"
          >
            <FolderIcon size={14} strokeWidth={1.5} className="text-fg-3" />
            <span>Home (root)</span>
            {currentParent === null ? (
              <span className="ml-auto lf-caption text-fg-3">current</span>
            ) : null}
          </button>
        </li>
        {sorted.map(({ folder, path }) => (
          <li key={folder.id}>
            <button
              type="button"
              onClick={() => onPick(folder.id)}
              disabled={currentParent === folder.id}
              className="flex w-full items-center gap-s-2 px-s-3 py-s-2 text-left lf-body-sm text-fg-1 hover:bg-surface-soft disabled:cursor-default disabled:opacity-50"
            >
              <FolderIcon size={14} strokeWidth={1.5} className="text-fg-3" />
              <span className="truncate">{path}</span>
              {currentParent === folder.id ? (
                <span className="ml-auto lf-caption text-fg-3">current</span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function pathOf(folder: Folder, byId: Map<string, Folder>): string {
  const parts: string[] = [folder.name]
  let cursor: string | null = folder.parentId
  const seen = new Set<string>()
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    const f = byId.get(cursor)
    if (!f) break
    parts.unshift(f.name)
    cursor = f.parentId
  }
  return parts.join(' / ')
}

function PermissionBadge({ permission }: { permission: Permission }) {
  const Icon = permission === 'public' ? Globe2 : permission === 'domain' ? Users : Lock
  return (
    <span
      title={PERMISSION_HELP[permission]}
      aria-label={PERMISSION_LABEL[permission]}
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-fg-3"
    >
      <Icon size={14} strokeWidth={1.5} />
    </span>
  )
}

const MAX_HTML_BYTES = 50 * 1024 * 1024

function UploadView({
  initialFolderId,
  onCancel,
  onSaved,
}: {
  initialFolderId: string | null
  onCancel: () => void
  onSaved: () => void
}) {
  const [kind, setKind] = useState<ArtifactType>('html')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [html, setHtml] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [permission, setPermission] = useState<Permission>('private')
  const [recipients, setRecipients] = useState<string[]>([])
  const [parentId, setParentId] = useState<string | null>(initialFolderId)
  const [folders, setFolders] = useState<Folder[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pull the folder list so the user can re-target. Fire once on mount —
  // failures are non-fatal (they just won't get a picker, but root upload
  // still works via initialFolderId).
  useEffect(() => {
    void listFolders()
      .then(setFolders)
      .catch(() => undefined)
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (busy) return
      setError(null)
      setBusy(true)
      try {
        const created = await createArtifact({
          title: title.trim(),
          description: description.trim() || undefined,
          type: kind,
          html: kind === 'html' ? html : undefined,
          linkUrl: kind === 'link' ? linkUrl.trim() : undefined,
          permission,
          recipients: permission === 'private' && recipients.length > 0 ? recipients : undefined,
          parentId,
        })
        // Fire-and-forget Slack-card capture while the user moves on.
        if (kind === 'html' && created?.id) {
          void captureAndUploadThumbnail(created.id, html)
        }
        onSaved()
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
        setBusy(false)
      }
    },
    [busy, description, html, kind, linkUrl, onSaved, parentId, permission, recipients, title],
  )

  return (
    <form onSubmit={handleSubmit} className="pt-s-9">
      <button
        type="button"
        onClick={onCancel}
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        Back to artifacts
      </button>

      <p className="mt-s-6 lf-eyebrow">▪ New artifact</p>
      <h1 className="mt-s-3 lf-display-2">New artifact.</h1>

      <div className="mt-s-7 inline-flex border border-line-hairline p-0.5">
        <button
          type="button"
          onClick={() => setKind('html')}
          className={`inline-flex items-center gap-s-2 px-s-4 py-s-2 lf-body-sm transition-colors duration-base ease-standard ${
            kind === 'html' ? 'bg-brand-pink text-brand-brown' : 'text-fg-3 hover:text-fg-1'
          }`}
        >
          <FileText size={14} strokeWidth={1.5} />
          HTML file
        </button>
        <button
          type="button"
          onClick={() => setKind('link')}
          className={`inline-flex items-center gap-s-2 px-s-4 py-s-2 lf-body-sm transition-colors duration-base ease-standard ${
            kind === 'link' ? 'bg-brand-pink text-brand-brown' : 'text-fg-3 hover:text-fg-1'
          }`}
        >
          <LinkIcon size={14} strokeWidth={1.5} />
          Web link
        </button>
      </div>

      <div className="mt-s-7 grid grid-cols-1 gap-s-6 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-s-6">
          <Field label="Title" hint="What to call this artifact in the list.">
            <input
              type="text"
              required
              maxLength={280}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={kind === 'html' ? 'Soft-glow daily.' : '2026 Beauty market dashboard.'}
              className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-body text-fg-1 placeholder:text-fg-3 focus:border-line-strong focus-visible:outline-none focus-visible:shadow-focus"
            />
          </Field>

          <Field label="Description" hint="Optional. One line you'll see in the list.">
            <input
              type="text"
              maxLength={1000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Aurora moodboard activated."
              className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-body text-fg-1 placeholder:text-fg-3 focus:border-line-strong focus-visible:outline-none focus-visible:shadow-focus"
            />
          </Field>

          {kind === 'html' ? (
            <Field
              label="HTML file"
              hint={
                fileName
                  ? `${fileName} · ${formatBytes(new Blob([html]).size)} of 50 MB.`
                  : 'Drop a Claude-generated HTML file here, or click to pick one.'
              }
            >
              <HtmlDropZone
                fileName={fileName}
                html={html}
                onLoaded={(name, content) => {
                  setFileName(name)
                  setHtml(content)
                  if (!title.trim()) {
                    const guess = name.replace(/\.(html?|htm)$/i, '').trim()
                    if (guess) setTitle(guess)
                  }
                  setError(null)
                }}
                onClear={() => {
                  setFileName(null)
                  setHtml('')
                }}
                onError={setError}
              />
            </Field>
          ) : (
            <Field
              label="URL"
              hint="A full https:// link to a doc, dashboard, or page you want to share."
            >
              <input
                type="url"
                required
                inputMode="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://docs.google.com/..."
                className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-body text-fg-1 placeholder:text-fg-3 focus:border-line-strong focus-visible:outline-none focus-visible:shadow-focus"
              />
            </Field>
          )}
        </div>

        <aside className="flex flex-col gap-s-6">
          <FolderPicker
            folders={folders}
            value={parentId}
            onChange={setParentId}
          />

          <ShareControls
            permission={permission}
            setPermission={setPermission}
            recipients={recipients}
            setRecipients={setRecipients}
          />

          {error ? (
            <p className="lf-body-sm text-status-danger">▪ {error}</p>
          ) : null}

          <div className="flex flex-col gap-s-2">
            <button
              type="submit"
              disabled={
                busy ||
                !title.trim() ||
                (kind === 'html' ? !html.trim() : !linkUrl.trim())
              }
              className="inline-flex w-full items-center justify-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Saving...' : 'Save artifact'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex w-full items-center justify-center px-s-5 py-s-3 lf-body-sm text-fg-3 transition-colors duration-base ease-standard hover:text-fg-1"
            >
              Cancel
            </button>
          </div>
        </aside>
      </div>
    </form>
  )
}

function EditModal({
  artifact,
  onClose,
  onSaved,
}: {
  artifact: Artifact
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = useState(artifact.title)
  const [description, setDescription] = useState(artifact.description ?? '')
  const [html, setHtml] = useState<string>('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [linkUrl, setLinkUrl] = useState(artifact.linkUrl ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const linkUrlChanged =
    artifact.type === 'link' && linkUrl.trim() !== (artifact.linkUrl ?? '')

  const dirty =
    title.trim() !== artifact.title ||
    description.trim() !== (artifact.description ?? '') ||
    !!html ||
    linkUrlChanged

  const handleSave = useCallback(async () => {
    if (busy || !dirty) return
    if (!title.trim()) {
      setError('Title is required.')
      return
    }
    if (artifact.type === 'link' && !linkUrl.trim()) {
      setError('URL is required.')
      return
    }
    setError(null)
    setBusy(true)
    try {
      await updateArtifact(artifact.id, {
        title: title.trim(),
        description: description.trim(),
        ...(html ? { html } : {}),
        ...(linkUrlChanged ? { linkUrl: linkUrl.trim() } : {}),
      })
      // Re-capture thumbnail when the HTML body was replaced.
      if (html && artifact.type === 'html') {
        void captureAndUploadThumbnail(artifact.id, html)
      }
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setBusy(false)
    }
  }, [artifact.id, artifact.type, busy, description, dirty, html, linkUrl, linkUrlChanged, onSaved, title])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand-brown/40 p-s-3 sm:p-s-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-title"
        className="w-full max-w-[560px] bg-surface-base shadow-lf-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line-hairline px-s-6 py-s-4">
          <div className="min-w-0">
            <p className="lf-eyebrow">▪ Edit</p>
            <h2 id="edit-title" className="mt-s-1 lf-h3 truncate">
              {artifact.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex flex-col gap-s-5 px-s-6 py-s-6">
          <Field label="Title" hint="What to call this artifact in the list.">
            <input
              type="text"
              required
              maxLength={280}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-body text-fg-1 focus:border-line-strong focus-visible:outline-none focus-visible:shadow-focus"
            />
          </Field>

          <Field label="Description" hint="Optional. One line you'll see in the list.">
            <input
              type="text"
              maxLength={1000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-body text-fg-1 focus:border-line-strong focus-visible:outline-none focus-visible:shadow-focus"
            />
          </Field>

          {artifact.type === 'html' ? (
            <Field
              label="Replace file"
              hint={
                fileName
                  ? `${fileName} · ${formatBytes(new Blob([html]).size)} — will replace the current contents on save.`
                  : `Optional. Current: ${formatBytes(artifact.sizeBytes)}. Drop a new .html to replace.`
              }
            >
              <HtmlDropZone
                fileName={fileName}
                html={html}
                onLoaded={(name, content) => {
                  setFileName(name)
                  setHtml(content)
                  setError(null)
                }}
                onClear={() => {
                  setFileName(null)
                  setHtml('')
                }}
                onError={setError}
              />
            </Field>
          ) : (
            <Field label="URL" hint="The web page this artifact points to.">
              <input
                type="url"
                required
                inputMode="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://docs.google.com/..."
                className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-body text-fg-1 placeholder:text-fg-3 focus:border-line-strong focus-visible:outline-none focus-visible:shadow-focus"
              />
            </Field>
          )}

          {error ? <p className="lf-body-sm text-status-danger">▪ {error}</p> : null}
        </div>

        <div className="flex items-center justify-end gap-s-2 border-t border-line-hairline px-s-6 py-s-4">
          <button
            type="button"
            onClick={onClose}
            className="px-s-4 py-s-2 lf-body-sm text-fg-3 transition-colors duration-base ease-standard hover:text-fg-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy || !dirty}
            className="rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-body-sm text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ShareModal({
  artifact,
  onClose,
  onSaved,
}: {
  artifact: Artifact
  onClose: () => void
  onSaved: () => void
}) {
  const [permission, setPermission] = useState<Permission>(artifact.permission)
  const [recipients, setRecipients] = useState<string[]>(artifact.recipients)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const dirty =
    permission !== artifact.permission ||
    recipients.join(',') !== artifact.recipients.join(',')

  const handleSave = useCallback(async () => {
    if (busy || !dirty) return
    setError(null)
    setBusy(true)
    try {
      await updateArtifact(artifact.id, {
        permission,
        recipients: permission === 'private' ? recipients : [],
      })
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setBusy(false)
    }
  }, [artifact.id, busy, dirty, onSaved, permission, recipients])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(artifact.viewUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard might be blocked; fall back to no-op.
    }
  }, [artifact.viewUrl])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand-brown/40 p-s-3 sm:p-s-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        className="w-full max-w-[480px] bg-surface-base shadow-lf-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line-hairline px-s-6 py-s-4">
          <div>
            <p className="lf-eyebrow">▪ Share</p>
            <h2 id="share-title" className="mt-s-1 lf-h3 truncate">
              {artifact.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center text-fg-3 transition-colors duration-base ease-standard hover:bg-surface-soft hover:text-fg-1"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex flex-col gap-s-6 px-s-6 py-s-6">
          <ShareControls
            permission={permission}
            setPermission={setPermission}
            recipients={recipients}
            setRecipients={setRecipients}
          />

          <div className="flex items-center gap-s-2 border-t border-line-hairline pt-s-4">
            <code className="flex-1 truncate rounded-r-1 bg-surface-soft px-s-3 py-s-2 font-text text-[12px] text-fg-2">
              {artifact.viewUrl}
            </code>
            <button
              type="button"
              onClick={() => void handleCopyLink()}
              className="shrink-0 rounded-r-1 border border-line-hairline px-s-3 py-s-2 lf-body-sm text-fg-1 transition-colors duration-base ease-standard hover:bg-surface-soft"
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>

          {error ? <p className="lf-body-sm text-status-danger">▪ {error}</p> : null}
        </div>

        <div className="flex items-center justify-end gap-s-2 border-t border-line-hairline px-s-6 py-s-4">
          <button
            type="button"
            onClick={onClose}
            className="px-s-4 py-s-2 lf-body-sm text-fg-3 transition-colors duration-base ease-standard hover:text-fg-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy || !dirty}
            className="rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-body-sm text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

function FolderPicker({
  folders,
  value,
  onChange,
}: {
  folders: Folder[]
  value: string | null
  onChange: (id: string | null) => void
}) {
  // Render the same path-style list as MoveToPicker so the user picks
  // a target the same way they re-target later.
  const sorted = useMemo(() => {
    const byId = new Map(folders.map((f) => [f.id, f]))
    return [...folders]
      .map((f) => ({ folder: f, path: pathOf(f, byId) }))
      .sort((a, b) => a.path.localeCompare(b.path))
  }, [folders])

  return (
    <div className="flex flex-col gap-s-2">
      <p className="lf-caption text-fg-2">Folder</p>
      <select
        value={value ?? 'root'}
        onChange={(e) => onChange(e.target.value === 'root' ? null : e.target.value)}
        className="w-full cursor-pointer rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 focus:border-line-strong focus-visible:outline-none focus-visible:shadow-focus"
      >
        <option value="root">Home (root)</option>
        {sorted.map(({ folder, path }) => (
          <option key={folder.id} value={folder.id}>
            {path}
          </option>
        ))}
      </select>
      <p className="lf-caption text-fg-3">Where this artifact lives. You can move it later.</p>
    </div>
  )
}

function ShareControls({
  permission,
  setPermission,
  recipients,
  setRecipients,
}: {
  permission: Permission
  setPermission: (p: Permission) => void
  recipients: string[]
  setRecipients: (r: string[]) => void
}) {
  return (
    <div className="flex flex-col gap-s-4">
      <div>
        <p className="lf-caption text-fg-2">Who can see it</p>
        <div className="mt-s-2 flex flex-col gap-s-2">
          {(['private', 'domain', 'public'] as Permission[]).map((p) => (
            <PermissionOption
              key={p}
              value={p}
              current={permission}
              onChange={setPermission}
            />
          ))}
        </div>
      </div>

      {permission === 'private' ? (
        <div>
          <p className="lf-caption text-fg-2">Specific people</p>
          <RecipientsInput
            recipients={recipients}
            setRecipients={setRecipients}
          />
        </div>
      ) : null}
    </div>
  )
}

function PermissionOption({
  value,
  current,
  onChange,
}: {
  value: Permission
  current: Permission
  onChange: (v: Permission) => void
}) {
  const selected = value === current
  const Icon = value === 'public' ? Globe2 : value === 'domain' ? Users : Lock
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={`flex items-start gap-s-3 rounded-r-1 border p-s-3 text-left transition-colors duration-base ease-standard ${
        selected
          ? 'border-brand-pink bg-pink-50'
          : 'border-line-hairline bg-surface-base hover:border-line-soft'
      }`}
    >
      <Icon size={16} strokeWidth={1.5} className={selected ? 'text-pink-700' : 'text-fg-3'} />
      <div className="flex flex-col gap-s-1">
        <span className="lf-h4">{PERMISSION_LABEL[value]}</span>
        <span className="lf-body-sm text-fg-3">{PERMISSION_HELP[value]}</span>
      </div>
    </button>
  )
}

function HtmlDropZone({
  fileName,
  html,
  onLoaded,
  onClear,
  onError,
}: {
  fileName: string | null
  html: string
  onLoaded: (name: string, content: string) => void
  onClear: () => void
  onError: (msg: string) => void
}) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const accept = useCallback(
    (file: File) => {
      if (file.size > MAX_HTML_BYTES) {
        onError('File is larger than the 50 MB cap.')
        return
      }
      const looksHtml = /\.(html?|htm)$/i.test(file.name) || file.type === 'text/html'
      if (!looksHtml) {
        onError('Pick an .html file. Other formats are not supported yet.')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const content = typeof reader.result === 'string' ? reader.result : ''
        if (!content.trim()) {
          onError('That file is empty.')
          return
        }
        onLoaded(file.name, content)
      }
      reader.onerror = () => onError('Could not read the file.')
      reader.readAsText(file)
    },
    [onError, onLoaded],
  )

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) accept(file)
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`relative flex min-h-[200px] flex-col items-center justify-center rounded-r-1 border-2 border-dashed bg-surface-base p-s-6 transition-colors duration-base ease-standard ${
        dragOver
          ? 'border-brand-pink bg-pink-50'
          : fileName
            ? 'border-line-soft'
            : 'border-line-soft hover:border-line-strong'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".html,.htm,text/html"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) accept(file)
          e.target.value = ''
        }}
        className="hidden"
      />
      {fileName ? (
        <div className="flex w-full flex-col items-center gap-s-3">
          <FileText size={28} strokeWidth={1.5} className="text-fg-3" />
          <div className="text-center">
            <p className="lf-h4 text-fg-1">{fileName}</p>
            <p className="mt-s-1 lf-caption text-fg-3">
              <span className="lf-num">{formatBytes(new Blob([html]).size)}</span> · ready
            </p>
          </div>
          <div className="flex items-center gap-s-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="rounded-r-1 border border-line-hairline px-s-3 py-s-2 lf-body-sm text-fg-1 hover:bg-surface-soft"
            >
              Replace file
            </button>
            <button
              type="button"
              onClick={onClear}
              className="lf-body-sm text-fg-3 hover:text-fg-1"
            >
              Clear
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center gap-s-3 text-center"
        >
          <Upload size={28} strokeWidth={1.5} className="text-fg-3" />
          <div>
            <p className="lf-h4 text-fg-1">Drop an .html file here.</p>
            <p className="mt-s-1 lf-body-sm text-fg-3">
              Or click to pick one. <span className="lf-num">50 MB</span> max.
            </p>
          </div>
        </button>
      )}
    </div>
  )
}

function RecipientsInput({
  recipients,
  setRecipients,
}: {
  recipients: string[]
  setRecipients: (r: string[]) => void
}) {
  const [draft, setDraft] = useState('')
  const [warning, setWarning] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([])
  const [activeSuggestion, setActiveSuggestion] = useState(0)
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const lowered = useMemo(() => new Set(recipients), [recipients])

  const tryAdd = useCallback(
    (raw: string) => {
      const email = raw.trim().toLowerCase()
      if (!email) return
      const suffix = `@${ALLOWED_DOMAIN}`
      const ok = /^[^\s@]+@[^\s@]+$/.test(email) && email.endsWith(suffix)
      if (!ok) {
        setWarning(`Only @${ALLOWED_DOMAIN} addresses are accepted.`)
        return
      }
      if (lowered.has(email)) {
        setWarning(`${email} is already on the list.`)
        return
      }
      setRecipients([...recipients, email])
      setDraft('')
      setSuggestions([])
      setWarning(null)
    },
    [lowered, recipients, setRecipients],
  )

  // Fetch suggestions with debounce. Skip if the draft already contains @ at
  // the end — the user is mid-domain and there's nothing to suggest yet.
  useEffect(() => {
    const trimmed = draft.trim()
    if (trimmed.length < 1) {
      setSuggestions([])
      return
    }
    const id = window.setTimeout(() => {
      void suggestUsers(trimmed).then((users) => {
        const filtered = users.filter((u) => !lowered.has(u.email))
        setSuggestions(filtered)
        setActiveSuggestion(0)
      })
    }, 200)
    return () => window.clearTimeout(id)
  }, [draft, lowered])

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault()
      setActiveSuggestion((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1
        return (next + suggestions.length) % suggestions.length
      })
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (suggestions.length > 0) {
        tryAdd(suggestions[activeSuggestion]?.email ?? draft)
      } else {
        tryAdd(draft)
      }
      return
    }
    if (e.key === ',' || (e.key === ' ' && draft.includes('@'))) {
      e.preventDefault()
      tryAdd(draft)
    } else if (e.key === 'Backspace' && draft === '' && recipients.length > 0) {
      setRecipients(recipients.slice(0, -1))
    } else if (e.key === 'Escape') {
      setSuggestions([])
    }
  }

  const handleBlur = () => {
    // Delay so suggestion mousedown still registers
    window.setTimeout(() => {
      setFocused(false)
      setSuggestions([])
      if (draft.trim() && draft.includes('@')) tryAdd(draft)
    }, 120)
  }

  const remove = (email: string) =>
    setRecipients(recipients.filter((r) => r !== email))

  return (
    <div className="relative mt-s-2">
      <div
        onClick={() => inputRef.current?.focus()}
        className="flex flex-wrap items-center gap-s-1 rounded-r-1 border border-line-soft bg-surface-base px-s-2 py-s-2 focus-within:border-line-strong focus-within:shadow-focus"
      >
        {recipients.map((r) => (
          <span
            key={r}
            className="inline-flex items-center gap-1 rounded-r-pill bg-surface-soft px-s-2 py-1 lf-body-sm text-fg-1"
          >
            <span className="lf-num">{r}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                remove(r)
              }}
              aria-label={`Remove ${r}`}
              className="inline-flex h-4 w-4 items-center justify-center rounded-r-pill text-fg-3 hover:bg-surface-base hover:text-fg-1"
            >
              <X size={10} strokeWidth={2} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            if (warning) setWarning(null)
          }}
          onKeyDown={handleKey}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          placeholder={recipients.length === 0 ? `name@${ALLOWED_DOMAIN}` : ''}
          className="min-w-[120px] flex-1 border-0 bg-transparent px-s-1 py-1 lf-body-sm text-fg-1 placeholder:text-fg-3 focus:outline-none"
        />
      </div>
      {focused && suggestions.length > 0 ? (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-[240px] overflow-y-auto border border-line-hairline bg-surface-base shadow-lf-2">
          {suggestions.map((u, i) => (
            <li key={u.email}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  tryAdd(u.email)
                }}
                onMouseEnter={() => setActiveSuggestion(i)}
                className={`flex w-full items-center gap-s-2 px-s-3 py-s-2 text-left lf-body-sm transition-colors ${
                  i === activeSuggestion
                    ? 'bg-pink-50 text-fg-1'
                    : 'text-fg-1 hover:bg-surface-soft'
                }`}
              >
                {u.picture ? (
                  <img
                    src={u.picture}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="h-5 w-5 rounded-r-pill object-cover"
                  />
                ) : (
                  <span className="inline-block h-5 w-5 rounded-r-pill bg-surface-soft" />
                )}
                <span className="flex min-w-0 flex-1 flex-col">
                  {u.name ? (
                    <span className="truncate text-fg-1">{u.name}</span>
                  ) : null}
                  <span
                    className={`truncate lf-num ${u.name ? 'lf-caption text-fg-3' : 'text-fg-1'}`}
                  >
                    {u.email}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <p className={`mt-s-1 lf-caption ${warning ? 'text-status-danger' : 'text-fg-3'}`}>
        {warning ?? `Type a name or email. Only registered @${ALLOWED_DOMAIN} users.`}
      </p>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-s-2">
      <span className="lf-caption text-fg-2">{label}</span>
      {children}
      {hint ? <span className="lf-caption text-fg-3">{hint}</span> : null}
    </label>
  )
}
